import {ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HttpErrorResponse} from '@angular/common/http';
import {EnrollmentCodePanelComponent} from './enrollment-code-panel/enrollment-code-panel.component';
import {DeviceEnrollmentHttpService} from './data-access/device-enrollment-http.service';
import {
  DeviceEnrollmentCompletedEvent, DeviceEnrollmentEvent, DeviceEnrollmentFailedEvent,
  DeviceEnrollmentRealtimeService, DeviceEnrollmentRedeemedEvent, EnrollmentDeviceNameOrigin
} from './data-access/device-enrollment-realtime.service';
import {
  DEVICE_RENAME_SAVED, DeviceEnrollmentSession, deviceRenameErrorMessage, enrollmentErrorMessage,
  enrollmentFailureMessage
} from './shared/device-enrollment.models';
import {PermissionService} from '../../_auth/permission.service';
import {SignalRHttpService} from '../signalRTracker/x-http-requests/signal-r-http.service';

/**
 * How many times a session is renewed **silently** as its backstop approaches before the panel is allowed to
 * lapse. Three renewals on a ten-minute session keep a screen live for about forty minutes — far longer than
 * any walk to a till — while an onboarding page left open overnight stops minting sessions, each of which is
 * a shared secret on an unattended screen. The operator's *Neuen Code anzeigen* resets the count.
 */
export const ENROLLMENT_MAX_SILENT_RENEWALS = 3;

/**
 * How long the screen waits, after a code was redeemed, before it says out loud that this is taking longer than
 * usual (#647).
 *
 * The setup behind a redeem is not instant — a first fiscal registration talks to the provider inside the 30 s
 * legal signing budget — so a minute is deliberately generous: it must not fire during a perfectly healthy
 * enrolment. What it must never do is time out into a *verdict*. When it elapses the screen adds one neutral
 * line and keeps waiting; the completed/failed push remains the only thing that turns the card green or red.
 */
export const ENROLLMENT_PENDING_SLOW_MS = 60_000;

/**
 * What the screen currently knows about the session it was showing. Exactly one of these is in force at a time,
 * and each is reached only by a push naming the on-screen session.
 */
type EnrollmentOutcome =
  | ({kind: 'pending'} & DeviceEnrollmentRedeemedEvent)
  | ({kind: 'completed'} & DeviceEnrollmentCompletedEvent)
  | ({kind: 'failed'} & DeviceEnrollmentFailedEvent);

/**
 * The QR tab of the register-onboarding page: opens the enrolment session and shows its rotating code (#469,
 * step 5; account-free and rotating since #279; three-event outcome since #647). Rendered inside
 * `DeviceOnboardingComponent`, which owns the page shell (back link, heading, tab strip) — this component is
 * one tab's body.
 *
 * ### Nothing is issued on load
 *
 * A session starts counting down to its backstop the instant it exists, so it must not be opened while the
 * operator is still at their desk — they press *Code anzeigen* when they are ready to walk to the till. From
 * then on the code rotates itself (the panel's job) and the session renews itself (this component's job, see
 * {@link ENROLLMENT_MAX_SILENT_RENEWALS}); the operator sees a code that is always current.
 *
 * ### There is no account to choose (#279)
 *
 * A code used to name an **anchor account**, and this screen used to open with a picker of the accounts a
 * register could be signed in as, plus a *Konto für Kassen anlegen* action for the venue whose accounts all
 * belonged to people. Since #279 the code binds to the caller's **company + shard** and the register redeeming
 * it receives a token whose roles are exactly `[Register]` — nothing a till holds can be inherited from
 * anybody, because there is nobody to inherit from. So the picker, the create action, the account-list load and
 * the eligibility copy are gone, and the screen is one press: the Standort is the session's.
 *
 * ### Refusals render through this component's own banner
 *
 * Deliberately **not** `ErrorBannerComponent` — that one shows `Error · Bad Request` and discards `code` and
 * `description` entirely, so a named refusal routed through it would reach the operator as nothing at all. The
 * sentence is mapped from the error **code** (`enrollmentErrorMessage`); the backend's English `description`
 * is never rendered — of any of the three pushes either.
 *
 * ### Three events, three states — and "redeemed" is not "succeeded" (#647)
 *
 * An enrolment session is **single-use**, and what happens to it arrives in two beats:
 *
 * 1. **`EnrollmentRedeemed`** — the code is spent. It is stamped the instant the session is consumed, *before*
 *    the login funnel behind it runs, so it can say nothing about the device: there is no device row yet, no
 *    name, and no outcome. The panel is torn down at once (which stops its rotation and renewal timers), so a
 *    consumed code is never left on screen for someone to rescan into an "invalid token" error, and the screen
 *    enters a **pending** state naming the serial.
 * 2. **`EnrollmentCompleted`** *or* **`EnrollmentFailed`** — pushed from the login tail, mutually exclusive, and
 *    the only things that may turn the card green or red.
 *
 * Splitting them is what closes a gap this screen used to have: a login that failed *after* a successful redeem
 * (a contract device cap, a TSS refusal, a provider outage on first registration) still left it reading "Gerät
 * erfolgreich verbunden" while the register showed an error and the code was spent. Hence also
 * {@link ENROLLMENT_PENDING_SLOW_MS}: when neither push arrives the screen says so in a neutral line and goes on
 * waiting — it never times out into a success it was not told about.
 *
 * ### The name, and renaming it here
 *
 * `EnrollmentCompleted` carries the device's name **and where that name came from** — a device replacing an iPad
 * with the same serial silently adopts its predecessor's name (`Device.ResolveInheritedName`), which is helpful
 * and completely invisible unless the screen says so. The card therefore states the origin in words, and offers
 * the rename right there, where the operator is standing at the till they just set up, rather than sending them
 * to the device settings page to find a device they cannot yet identify. The rename posts the existing
 * `Device/Information/ChangeName`, and the block is rendered only when that permission is granted.
 *
 * Only an explicit *Weitere Geräte onboarden* leaves the outcome behind and opens a fresh session — none is
 * issued automatically. An event for a *different* session is ignored, for all three kinds (a colleague
 * onboarding elsewhere on the same shard must not clear this operator's live code, nor overwrite their card).
 */
@Component({
  selector: 'app-device-enrollment',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [EnrollmentCodePanelComponent],
  template: `
    <div class="flex flex-col gap-4">
      <!-- One live region for the whole tab, present from the first render so a state change is actually
           announced. The cards themselves carry no aria-live: a rename field inside a live region would have
           every keystroke read back. -->
      <p class="sr-only" role="status" aria-live="polite">{{ liveStatus() }}</p>

      @if (error(); as message) {
        <div class="rounded-2xl border border-red-100 bg-red-50 p-4 shadow-sm" role="alert">
          <p class="text-sm font-semibold text-red-700">{{ message }}</p>
        </div>
      }

      @if (pending(); as waiting) {
        <!-- The code is spent but the login behind it has not answered yet. Deliberately neutral: this is not a
             success state, and the only things that may make it one are the completed/failed pushes. -->
        <section class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6"
                 aria-labelledby="enrollment-pending-heading">
          <div class="flex items-start gap-3">
            <svg class="mt-0.5 h-5 w-5 shrink-0 animate-spin text-theme-primary-500" fill="none"
                 viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
            </svg>
            <div class="min-w-0">
              <h2 id="enrollment-pending-heading" class="text-base font-bold tracking-tight text-gray-800">
                Code eingelöst
              </h2>
              <p class="mt-1 text-sm text-gray-600">
                Die Kasse mit der Seriennummer
                <span class="font-mono font-semibold">{{ waiting.serialNumber }}</span> wird eingerichtet.
              </p>
              <p class="mt-2 text-xs text-gray-500">
                Bitte lassen Sie diese Seite geöffnet, bis die Einrichtung abgeschlossen ist.
              </p>
              @if (pendingSlow()) {
                <p class="mt-2 text-xs font-semibold text-gray-600">
                  Das dauert länger als üblich. Prüfen Sie die Anzeige auf der Kasse.
                </p>
              }
            </div>
          </div>
        </section>
      } @else if (completed(); as done) {
        <!-- A device consumed the code and the login behind it succeeded. The QR is gone (the panel unmounted
             with the session), so nothing on screen can be rescanned into an "invalid token". -->
        <section class="rounded-2xl border border-green-100 bg-green-50 p-4 shadow-sm sm:p-6"
                 aria-labelledby="enrollment-success-heading">
          <h2 id="enrollment-success-heading" class="mb-2 text-base font-bold tracking-tight text-green-800">
            Gerät erfolgreich verbunden
          </h2>
          <p class="text-sm text-green-800">
            Die Kasse mit der Seriennummer <span class="font-mono font-semibold">{{ done.serialNumber }}</span>
            wurde eingerichtet. Der angezeigte Code ist damit verbraucht.
          </p>

          @if (deviceName(); as current) {
            <p class="mt-2 text-sm text-green-800">
              Name: <span class="font-semibold">{{ current }}</span>
            </p>
          }
          <!-- The origin is the whole point of the second push: an inherited name and a name an administrator
               typed look identical, and only the server knows which this is. -->
          @if (originHint(); as hint) {
            <p class="mt-1 text-xs text-green-700">{{ hint }}</p>
          }

          @if (canChangeName) {
            <div class="mt-4 border-t border-green-100 pt-4">
              <label for="enrollment-device-name"
                     class="block text-sm font-bold tracking-tight text-green-800">Name der Kasse</label>
              <div class="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center">
                <input id="enrollment-device-name" type="text" autocomplete="off" [value]="nameInput()"
                       (input)="nameInput.set(value($event))" [disabled]="renaming()"
                       placeholder="z. B. Theke 1"
                       class="block w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm
                              focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500
                              sm:text-sm transition-all duration-200 placeholder-gray-300
                              disabled:cursor-not-allowed disabled:opacity-50"/>
                <button type="button" (click)="saveName()" [disabled]="!canSaveName()"
                        class="inline-flex shrink-0 items-center justify-center rounded-2xl border
                               border-green-200 bg-white px-5 py-2.5 text-sm font-semibold text-green-800
                               shadow-sm transition-all duration-200 hover:bg-green-100 active:scale-95
                               disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale">
                  Speichern
                </button>
              </div>

              @if (renameError(); as problem) {
                <p class="mt-2 text-xs font-semibold text-red-600" role="alert">{{ problem }}</p>
              } @else if (renameSaved()) {
                <p class="mt-2 text-xs font-semibold text-green-700">{{ renameSavedText }}</p>
              }
            </div>
          }

          <p class="mt-4 text-xs text-green-700">
            Möchten Sie eine weitere Kasse einrichten, lassen Sie einen neuen Code anzeigen.
          </p>

          <div class="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button type="button" (click)="issue()" [disabled]="busy()"
                    class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5
                           py-2.5 text-sm font-semibold text-white shadow-lg shadow-theme-primary-100
                           transition-all duration-200 hover:bg-theme-primary-700 hover:shadow-xl
                           active:scale-95 disabled:cursor-not-allowed disabled:opacity-30
                           disabled:grayscale">
              Weitere Geräte onboarden
            </button>
          </div>
        </section>
      } @else if (failed(); as problem) {
        <!-- The code was consumed and the login behind it failed. Saying "erfolgreich verbunden" here — which is
             what this screen did before #647 — is worse than saying nothing: the code is spent either way. -->
        <section class="rounded-2xl border border-red-100 bg-red-50 p-4 shadow-sm sm:p-6"
                 aria-labelledby="enrollment-failed-heading">
          <h2 id="enrollment-failed-heading" class="mb-2 text-base font-bold tracking-tight text-red-800">
            Einrichtung fehlgeschlagen
          </h2>
          <p class="text-sm text-red-800">
            Die Kasse mit der Seriennummer <span class="font-mono font-semibold">{{ problem.serialNumber }}</span>
            konnte nicht eingerichtet werden.
          </p>
          <p class="mt-2 text-sm text-red-800">{{ failureMessage() }}</p>
          <p class="mt-2 text-xs text-red-700">Der angezeigte Code ist verbraucht.</p>

          <div class="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button type="button" (click)="issue()" [disabled]="busy()"
                    class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5
                           py-2.5 text-sm font-semibold text-white shadow-lg shadow-theme-primary-100
                           transition-all duration-200 hover:bg-theme-primary-700 hover:shadow-xl
                           active:scale-95 disabled:cursor-not-allowed disabled:opacity-30
                           disabled:grayscale">
              Neuen Code anzeigen
            </button>
          </div>
        </section>
      } @else if (!session()) {
        <!-- The initial state: only shown while no code is displayed and nothing has been redeemed yet. Once a
             code is on screen this card is hidden so the operator sees only the live QR panel. -->
        <section class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6"
                 aria-labelledby="enrollment-issue-heading">
          <h2 id="enrollment-issue-heading" class="mb-2 text-base font-bold tracking-tight text-gray-800">
            Code für diesen Standort
          </h2>
          <!-- States what the code IS, so nobody looks for the account picker this screen used to open with:
               the till gets Kassenrechte and nothing else, and no account is involved (#279). -->
          <p class="text-sm text-gray-600">
            Der Code gilt für diesen Standort. Eine Kasse, die ihn scannt, erhält ausschließlich Kassenrechte —
            ein Konto wird dafür nicht benötigt.
          </p>
          <p class="mt-2 text-xs text-gray-500">
            Der Code erneuert sich jede Minute von selbst. Lassen Sie ihn erst anzeigen, wenn Sie das Gerät zur
            Hand haben.
          </p>

          <div class="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button type="button" (click)="issue()" [disabled]="busy()"
                    class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5
                           py-2.5 text-sm font-semibold text-white shadow-lg shadow-theme-primary-100
                           transition-all duration-200 hover:bg-theme-primary-700 hover:shadow-xl
                           active:scale-95 disabled:cursor-not-allowed disabled:opacity-30
                           disabled:grayscale">
              Code anzeigen
            </button>
          </div>
        </section>
      }

      @if (session(); as opened) {
        <device-enrollment-code-panel [session]="opened" [busy]="busy()" (reissue)="issue()" (renew)="renew()"/>
      }
    </div>
  `
})
export class DeviceEnrollmentComponent {
  private readonly http = inject(DeviceEnrollmentHttpService);
  private readonly realtime = inject(DeviceEnrollmentRealtimeService);
  private readonly devices = inject(SignalRHttpService);
  private readonly permissions = inject(PermissionService);

  protected readonly session = signal<DeviceEnrollmentSession | null>(null);
  protected readonly busy = signal(false);
  protected readonly error = signal<string | null>(null);

  /** What became of the session that was on screen — the one state the three pushes write. */
  private readonly outcome = signal<EnrollmentOutcome | null>(null);

  /** Narrowed views of {@link outcome}, so the template branches on a precisely typed value. */
  protected readonly pending = computed(() => {
    const state = this.outcome();
    return state?.kind === 'pending' ? state : null;
  });
  protected readonly completed = computed(() => {
    const state = this.outcome();
    return state?.kind === 'completed' ? state : null;
  });
  protected readonly failed = computed(() => {
    const state = this.outcome();
    return state?.kind === 'failed' ? state : null;
  });

  /** Set once {@link ENROLLMENT_PENDING_SLOW_MS} passes with no outcome. Adds a line; decides nothing. */
  protected readonly pendingSlow = signal(false);

  /**
   * The name currently in force for the enrolled device — the pushed one, then whatever a successful rename put
   * there. Empty for a device that has none yet, which is why the card shows the line only when it is filled.
   */
  protected readonly deviceName = signal('');

  /** The rename field. Controlled (`[value]` + `(input)`), per the style guide's prefilled-field rule. */
  protected readonly nameInput = signal('');
  protected readonly renaming = signal(false);
  protected readonly renameSaved = signal(false);
  protected readonly renameError = signal<string | null>(null);

  /** The confirmation sentence, held as a field so the template never carries a literal the specs also assert. */
  protected readonly renameSavedText = DEVICE_RENAME_SAVED;

  /**
   * Whether this administrator may rename a device at all. Read once: a permission set does not change within a
   * session, and the block is simply absent when it is not granted — an input that always answers 403 is worse
   * than no input.
   */
  protected readonly canChangeName =
    this.permissions.CheckPermission(this.permissions.Device_Information_ChangeName());

  /** Saving is offered only for a real, changed name — and never twice at once. */
  protected readonly canSaveName = computed(() => {
    const typed = this.nameInput().trim();
    return !this.renaming() && typed.length > 0 && typed !== this.deviceName();
  });

  /**
   * The sentence that states where the device's name came from — and only while it is still true.
   *
   * The origin describes the name the enrolment *arrived* with. Once the operator has replaced that name from
   * this very card, "der Name wurde übernommen" (or "es hat noch keinen Namen", beside a name) is a statement
   * about a state that no longer exists, so it is dropped rather than left to contradict the line above it.
   */
  protected readonly originHint = computed(() =>
    this.renameSaved() ? '' : originSentence(this.completed()?.nameOrigin));

  /** The German sentence for a failed enrolment — mapped from the code, never the English `description`. */
  protected readonly failureMessage = computed(() => enrollmentFailureMessage(this.failed()?.errorCode));

  /**
   * What the tab's live region announces. One sentence per state, so a screen-reader user learns that the code
   * was consumed, and then what became of it, without polling the page.
   */
  protected readonly liveStatus = computed(() => {
    const state = this.outcome();
    if (!state) return '';
    switch (state.kind) {
      case 'pending':
        return `Code eingelöst. Die Kasse mit der Seriennummer ${state.serialNumber} wird eingerichtet.`;
      case 'completed':
        return `Die Kasse mit der Seriennummer ${state.serialNumber} wurde eingerichtet.`;
      case 'failed':
        return `Die Kasse mit der Seriennummer ${state.serialNumber} konnte nicht eingerichtet werden.`;
    }
  });

  /** The pending-state timer, held so a state change or a destroy can cancel it. */
  private slowTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Listen for the whole life of the tab. An event is acted on only when it names the session on screen (or
    // the one whose outcome is being awaited), so a subscription that is always up costs nothing and needs no
    // start/stop dance as sessions come and go. The connection is torn down automatically when the component is
    // destroyed (takeUntilDestroyed).
    this.realtime.listen()
      .pipe(takeUntilDestroyed())
      .subscribe(event => this.onEvent(event));

    // A tab closed while a setup is pending must not leave a timer behind that fires into a destroyed view.
    inject(DestroyRef).onDestroy(() => this.stopSlowTimer());
  }

  /**
   * An enrolment event arrived. It is acted on only when it names the session this screen is displaying — or,
   * once the code has been consumed, the session whose outcome it is waiting for; otherwise it is ignored, for
   * every kind. Another operator onboarding elsewhere on this shard must neither clear a code this one is still
   * showing nor overwrite the card it is reading.
   */
  private onEvent(event: DeviceEnrollmentEvent): void {
    if (!this.watches(event.sessionId)) return;

    switch (event.kind) {
      case 'redeemed':
        return this.enterPending(event);
      case 'completed':
        return this.enterCompleted(event);
      case 'failed':
        return this.enterFailed(event);
    }
  }

  /** The sessions this screen still cares about: the one on display, and the one it is awaiting an outcome for. */
  private watches(sessionId: string): boolean {
    return sessionId === this.session()?.sessionId || sessionId === this.outcome()?.sessionId;
  }

  /**
   * The code was consumed. Tear the panel down (which stops the rotation and renewal timers with it) and wait —
   * this is explicitly **not** a success state, and nothing here may become one on a timer.
   */
  private enterPending(event: DeviceEnrollmentRedeemedEvent): void {
    this.session.set(null);
    // The tag goes LAST: the event arrives already carrying the realtime service's own `kind` ('redeemed'), and
    // spreading it over a leading `kind: 'pending'` would silently put that value back — an outcome no branch of
    // this component matches, so the screen would fall through to the intro card as if nothing had happened.
    this.outcome.set({...event, kind: 'pending'});

    this.stopSlowTimer();
    this.pendingSlow.set(false);
    this.slowTimer = setTimeout(() => {
      this.slowTimer = null;
      this.pendingSlow.set(true);
    }, ENROLLMENT_PENDING_SLOW_MS);
  }

  /** The login behind the redeem succeeded: name the device, state where its name came from, offer the rename. */
  private enterCompleted(event: DeviceEnrollmentCompletedEvent): void {
    this.session.set(null);
    this.stopSlowTimer();
    this.pendingSlow.set(false);
    this.outcome.set({...event, kind: 'completed'});

    // A brand-new device has no name yet — the server's `name` is then its own id, which is not something to
    // offer for editing. Prefilling anything else would invite the operator to "keep" a name that is really a
    // placeholder.
    const named = event.nameOrigin === 'New' ? '' : (event.name ?? '');
    this.deviceName.set(named);
    this.nameInput.set(named);
    this.renameSaved.set(false);
    this.renameError.set(null);
    this.renaming.set(false);
  }

  /** The login behind the redeem failed. The code is spent; say what went wrong and offer a fresh one. */
  private enterFailed(event: DeviceEnrollmentFailedEvent): void {
    this.session.set(null);
    this.stopSlowTimer();
    this.pendingSlow.set(false);
    this.outcome.set({...event, kind: 'failed'});
  }

  private stopSlowTimer(): void {
    if (this.slowTimer === null) return;
    clearTimeout(this.slowTimer);
    this.slowTimer = null;
  }

  /** The value a text input now holds — the controlled-field idiom from the style guide. */
  protected value(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  /**
   * Saves the typed name against the enrolled device through the existing `Device/Information/ChangeName`.
   *
   * The card is re-rendered from what was **saved**, not from the request: a duplicate name is refused with a
   * 409 and must leave the old name standing, otherwise the screen would show a name the installation does not
   * have.
   */
  protected saveName(): void {
    const target = this.completed();
    if (!target || !this.canChangeName || !this.canSaveName()) return;

    const typed = this.nameInput().trim();
    this.renaming.set(true);
    this.renameSaved.set(false);
    this.renameError.set(null);

    this.devices.changeName(target.deviceId, typed).subscribe({
      next: () => {
        this.deviceName.set(typed);
        this.nameInput.set(typed);
        this.renameSaved.set(true);
        this.renaming.set(false);
      },
      error: (err: HttpErrorResponse) => {
        this.renameError.set(deviceRenameErrorMessage(err));
        this.renaming.set(false);
      }
    });
  }

  /** Silent renewals since the operator last pressed a button. Bounded by {@link ENROLLMENT_MAX_SILENT_RENEWALS}. */
  private silentRenewals = 0;

  /**
   * Opens a fresh session for the session's shard. Also the re-issue path — the server retires whatever was
   * outstanding for the shard, so "display again" and "display for the first time" are genuinely the same call.
   * A press by the operator resets the silent-renewal budget: they are evidently still at the screen.
   */
  protected issue(): void {
    this.silentRenewals = 0;
    // Leave the outcome behind: *Weitere Geräte onboarden* / *Neuen Code anzeigen* start a new session, and the
    // previous device's name, rename field and messages have nothing to do with the next one.
    this.clearOutcome();
    this.open();
  }

  private clearOutcome(): void {
    this.stopSlowTimer();
    this.outcome.set(null);
    this.pendingSlow.set(false);
    this.deviceName.set('');
    this.nameInput.set('');
    this.renaming.set(false);
    this.renameSaved.set(false);
    this.renameError.set(null);
  }

  /**
   * The panel reports that its session's backstop is near. Renew silently while the budget lasts; past it the
   * panel is left to lapse and the operator sees *Neuen Code anzeigen* — a screen nobody is looking at must not
   * keep minting shared secrets forever.
   */
  protected renew(): void {
    if (this.silentRenewals >= ENROLLMENT_MAX_SILENT_RENEWALS) return;
    this.silentRenewals++;
    this.open();
  }

  private open(): void {
    if (this.busy()) return;

    this.busy.set(true);
    this.error.set(null);

    this.http.issue().subscribe({
      next: opened => {
        this.session.set(opened);
        this.busy.set(false);
      },
      error: (err: HttpErrorResponse) => {
        // The previous session is left on screen deliberately: a failed re-issue did not retire it, so it may
        // still be live, and clearing it would throw away a code that still works.
        this.error.set(enrollmentErrorMessage(err.error?.code));
        this.busy.set(false);
      }
    });
  }
}

/**
 * The sentence stating where a freshly enrolled device's name came from.
 *
 * A free function rather than a `Record` in the models module because all three values are exhaustive and
 * screen-specific: they are the copy of *this* card, not a vocabulary another surface will reuse.
 */
function originSentence(origin: EnrollmentDeviceNameOrigin | undefined): string {
  switch (origin) {
    case 'Inherited':
      return 'Der Name wurde vom vorherigen Gerät mit dieser Seriennummer übernommen.';
    case 'New':
      return 'Neues Gerät — es hat noch keinen Namen.';
    case 'Existing':
      return 'Dieses Gerät war bereits bekannt.';
    default:
      return '';
  }
}
